import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { ShieldCheck, Globe, Users, Award } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

// Animated counter hook
const useCountUp = (end, duration = 2000, start = 0) => {
  const [count, setCount] = useState(start);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * (end - start) + start));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, end, duration, start]);

  return { count, ref, isVisible };
};

export const AboutPage = () => {
  const { t, isRTL } = useLanguage();

  // Animated counters for client percentages
  const europeCounter = useCountUp(50, 2000);
  const middleEastCounter = useCountUp(40, 2000);
  const northAfricaCounter = useCountUp(10, 2000);

  const values = [
    { 
      icon: ShieldCheck, 
      title: t('about.point1Title'), 
      desc: t('about.point1Desc'),
      color: 'bg-blue-50 text-primary-blue'
    },
    { 
      icon: Globe, 
      title: t('about.point2Title'), 
      desc: t('about.point2Desc'),
      color: 'bg-red-50 text-accent-red'
    },
    { 
      icon: Users, 
      title: t('about.point3Title'), 
      desc: t('about.point3Desc'),
      color: 'bg-green-50 text-green-600'
    },
  ];

  return (
    <div className="min-h-screen pt-16" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 font-heading mb-6" data-testid="about-title">
              {t('about.title')}
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed" data-testid="about-subtitle">
              {t('about.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className={isRTL ? 'order-2' : 'order-1'}>
              <img
                src="https://images.unsplash.com/photo-1758599543152-a73184816eba?auto=format&fit=crop&w=800&q=80"
                alt="Business partnership"
                className="rounded-2xl shadow-xl w-full h-[450px] object-cover"
              />
            </div>
            <div className={`${isRTL ? 'order-1 text-right' : 'order-2 text-left'}`}>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-heading mb-6">
                {t('about.subtitle')}
              </h2>
              <p className="text-base text-slate-600 mb-6 leading-relaxed">
                {t('about.description')}
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                {t('about.experience')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="about-values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-3xl font-bold text-slate-900 font-heading mb-4">
              {t('about.coreValuesTitle')}
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              {t('about.coreValuesSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <Card 
                key={index} 
                className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid={`value-card-${index}`}
              >
                <CardContent className="p-8 text-center">
                  <div className={`w-16 h-16 ${value.color} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                    <value.icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {value.desc}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section className="py-16 md:py-20 bg-primary-blue" data-testid="about-clients-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white font-heading mb-10 text-center">
            {t('about.ourClients')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div 
              ref={europeCounter.ref}
              data-testid="stat-europe"
              className={`transform transition-all duration-700 ${europeCounter.isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
            >
              <div className="text-4xl sm:text-5xl font-bold text-white font-heading mb-2">
                {europeCounter.count}%
              </div>
              <div className="text-blue-200 text-sm">{t('about.europe')}</div>
            </div>
            <div 
              ref={middleEastCounter.ref}
              data-testid="stat-middleeast"
              className={`transform transition-all duration-700 delay-150 ${middleEastCounter.isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
            >
              <div className="text-4xl sm:text-5xl font-bold text-white font-heading mb-2">
                {middleEastCounter.count}%
              </div>
              <div className="text-blue-200 text-sm">{t('about.middleEast')}</div>
            </div>
            <div 
              ref={northAfricaCounter.ref}
              data-testid="stat-northafrica"
              className={`transform transition-all duration-700 delay-300 ${northAfricaCounter.isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
            >
              <div className="text-4xl sm:text-5xl font-bold text-white font-heading mb-2">
                {northAfricaCounter.count}%
              </div>
              <div className="text-blue-200 text-sm">{t('about.northAfrica')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="about-mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <Award className="w-16 h-16 text-primary-blue mx-auto mb-6" />
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-heading mb-6">
              {t('about.missionTitle')}
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              {t('about.missionDesc')}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
